import React from "react";
import styled from "styled-components";
import { Tabs } from "antd";
import {
  PlusOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { CardHeader } from "../shared";
import EmptyState from "../EmptyState";
import RepairItem from "./RepairItem";

const RepairTabsSection = ({
  activeTab,
  onTabChange,
  activeRepairs,
  scheduledRepairs,
  completedRepairs,
  onAddRepair,
  onEditRepair,
  draftActionsByTicketId = {},
  onApproveDraft,
}) => {
  const tabsItems = [
    {
      key: "active",
      label: `Active (${activeRepairs.length})`,
      children: (
        <CardHeader
          title="Active Repairs"
          subtitle="Currently in-progress maintenance requests"
          actionButton={<PlusOutlined />}
          actionButtonText="Add Repair"
          onActionClick={onAddRepair}
        >
          {activeRepairs.length === 0 ? (
            <EmptyState
              icon={<ToolOutlined />}
              title="No Active Repairs"
              description="All repairs are either scheduled or completed"
              ctaLabel="Add New Repair"
              ctaAction={onAddRepair}
              ctaIcon={
                <FilteredIcon>
                  <PlusOutlined />
                </FilteredIcon>
              }
            />
          ) : (
            <RepairGrid>
              {activeRepairs.map((item) => (
                <RepairItem
                  key={item.id || item.title}
                  repair={item}
                  onEdit={onEditRepair}
                  draftAction={draftActionsByTicketId[item.id]}
                  onApproveDraft={onApproveDraft}
                />
              ))}
            </RepairGrid>
          )}
        </CardHeader>
      ),
    },
    {
      key: "scheduled",
      label: `Scheduled (${scheduledRepairs.length})`,
      children: (
        <CardHeader
          title="Scheduled Repairs"
          subtitle="Upcoming maintenance appointments"
          actionButton={<PlusOutlined />}
          actionButtonText="Add Repair"
          onActionClick={onAddRepair}
        >
          {scheduledRepairs.length === 0 ? (
            <EmptyState
              icon={<ClockCircleOutlined />}
              title="No Scheduled Repairs"
              description="Schedule repairs to keep your units in top condition"
              ctaLabel="Schedule Repair"
              ctaAction={onAddRepair}
              ctaIcon={
                <FilteredIcon>
                  <PlusOutlined />
                </FilteredIcon>
              }
            />
          ) : (
            <RepairGrid>
              {scheduledRepairs.map((item) => (
                <RepairItem
                  key={item.id || item.title}
                  repair={item}
                  onEdit={onEditRepair}
                  draftAction={draftActionsByTicketId[item.id]}
                  onApproveDraft={onApproveDraft}
                />
              ))}
            </RepairGrid>
          )}
        </CardHeader>
      ),
    },
    {
      key: "completed",
      label: `Completed (${completedRepairs.length})`,
      children: (
        <CardHeader
          title="Completed Repairs"
          subtitle="Historical maintenance records"
          actionButton={<PlusOutlined />}
          actionButtonText="Add Repair"
          onActionClick={onAddRepair}
        >
          {completedRepairs.length === 0 ? (
            <EmptyState
              icon={<CheckCircleOutlined />}
              title="No Completed Repairs"
              description="Completed repairs will appear here for your records"
              ctaLabel="Add New Repair"
              ctaAction={onAddRepair}
              ctaIcon={
                <FilteredIcon>
                  <PlusOutlined />
                </FilteredIcon>
              }
            />
          ) : (
            <RepairGrid>
              {completedRepairs.map((item) => (
                <RepairItem
                  key={item.id || item.title}
                  repair={item}
                  onEdit={onEditRepair}
                  draftAction={draftActionsByTicketId[item.id]}
                  onApproveDraft={onApproveDraft}
                />
              ))}
            </RepairGrid>
          )}
        </CardHeader>
      ),
    },
  ];

  return (
    <TabsContainer>
      <Tabs activeKey={activeTab} onChange={onTabChange} items={tabsItems} />
    </TabsContainer>
  );
};

// Styled Components
const RepairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FilteredIcon = styled.div`
  filter: brightness(0) invert(1);
`;

const TabsContainer = styled.div`
  .ant-tabs-tab {
    color: #000000 !important;
    font-weight: 500 !important;

    &.ant-tabs-tab-active {
      color: var(--color-primary) !important;
      font-weight: 600 !important;
    }
  }

  .ant-tabs-tab-btn {
    color: inherit !important;
  }

  .ant-tabs-tab-remove {
    color: #000000 !important;
  }

  .ant-tabs-nav {
    margin-bottom: 1rem !important;
  }

  .ant-tabs-tab-label {
    color: inherit !important;
    font-weight: inherit !important;
  }

  .ant-tabs-content-holder {
    color: #000000 !important;
  }
`;

export default RepairTabsSection;
